import React, { useState, useMemo, useEffect, useRef } from "react";
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
import { useDashboard } from "./dashboardStore";

import FormattedNumberInput from "../utils/FormattedNumberInput";
import ReportExporter from "../utils/ReportExporter";
import StagingTable from "../utils/StagingTable";
import { socket } from "../utils/socket";


const placeholdersForModal = {
	name: "Product/Item Name",
	type: "Enter type/category",
	price: "Enter the unit price (e.g. 100)",
	quantity: "Enter quantity with unit (e.g. 10m or 10cm)",
	netWorth: "Enter Net Worth of products (eg 10000)",
	warehouse: "Enter Warehouse (eg 242)",
	default: "Enter value",
};


// Labels for fields
const fieldLabels = {
    name: "Product Name",
    type: "Product Category",
    price: "Price Per",
    quantity: "Quantity with unit",
    netWorth: "Total Price",
    warehouse: "Warehouse"
};

const UsersInventory = () => {
	const [items, setItems] = useState([]);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const [showModal, setShowModal] = useState(false);
	const [newItem, setNewItem] = useState({ name: "", type: "", price: "", quantity: "", netWorth: "", warehouse: "" });
	const [errors, setErrors] = useState({});
	const pageSize = 8;
	const [loading, setLoading] = useState(false);

     const parentRef = useRef(null);
     const [showStaging, setShowStaging] = useState(false);
     const stagingRef = useRef(null);
    
     // Scroll to staging table or parent based on toggle
     useEffect(() => {
         if (showStaging && stagingRef.current) {
             stagingRef.current.scrollIntoView({ behavior: "smooth" });
         }
    
         if (!showStaging && parentRef?.current) {
             parentRef.current.scrollIntoView({ behavior: "smooth" });
         }
     }, [showStaging]);
     
    // Socket: handle new item
    useEffect(() => {
        if (!socket) return;

        const handleNewItem = (data) => {
        	if (data.type === "singleNewItem") {
                const newItem = data.newItem;
                setItems((prev) => [newItem, ...prev]);
            }
            
            if (data.type === "itemDeleted") {
                setItems(prevItems => prevItems.filter(q => q.id !== Number(data.id)));
            }

            if (data.type === "batchItems") { //		io.emit("alertItems", { type: "batchItems", rows: insertedRows });

                    
               setItems(prev => [...data.rows, ...prev]);
            }
        }

        socket.on("alertItems", handleNewItem);
        return () => {
            socket.off("alertItems", handleNewItem);
        };
    }, [socket]);
     
    
// Subscribe to dashboard store slice
const inventoryData = useDashboard(state => state.data?.inventory);

// -------------------------------------
// Process mistakes requests data
// -------------------------------------
useEffect(() => {
  // Wait until store has data
  if (!inventoryData) return;

  try {
    // Save to state
			setItems(Array.isArray(inventoryData) ? inventoryData : []);
  } catch (err) {
    console.error("Error processing feature requests:", err);
    setItems([]);
  }
}, [inventoryData]); // Reactive: runs whenever store updates


	const validateItem = () => {
		const newErrors = {};
		Object.entries(newItem).forEach(([key, value]) => {
			if (!value) newErrors[key] = true;
		});
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleAdd = async (itemToAdd) => {
		if (!validateItem()) return;

		try {
			const res = await fetch(`${apiBaseUrl}/api/inventory`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					...itemToAdd,
					price: Number(itemToAdd.price) || 0,
					quantity: itemToAdd.quantity || 0,
					netWorth: Number(itemToAdd.netWorth) || 0,
				}),
			});
			if (!res.ok) throw new Error("Failed to add item");

			setPage(1);
			setShowModal(false);
			setNewItem({ name: "", type: "", price: "", quantity: "", netWorth: "", warehouse: "" });
			setErrors({});
		} catch (err) {
			console.error("Error adding item:", err);
			alert("Failed to add item. Try again.");
		}
	};

	const handleDelete = async (id) => {
  		const confirmDelete = window.confirm(
  			"⚠️ Are you sure you want to delete this quotation?"
  		);
  		if (!confirmDelete) return;
  		
		try {
			const res = await fetch(`${apiBaseUrl}/api/inventory/${id}`, {
				method: "DELETE",
				headers: { "Content-Type": "application/json" },
			});
			if (!res.ok) throw new Error("Failed to delete item");
		} catch (err) {
			console.error("Error deleting item:", err);
			alert("Failed to delete item. Try again.");
		}
	};
// Filter items based on search
const filteredItems = useMemo(() => {
	try {
		const safeItems = Array.isArray(items) ? items : [];
		const sorted = [...safeItems].sort((a, b) => (b?.id || 0) - (a?.id || 0));
		return sorted.filter((i) =>
			(i?.name?.toLowerCase() ?? "").includes(search.toLowerCase()) ||
			(i?.type?.toLowerCase() ?? "").includes(search.toLowerCase()) ||
			(i?.warehouse?.toLowerCase() ?? "").includes(search.toLowerCase())
		);
	} catch (err) {
		console.error("Error filtering items:", err);
		return [];
	}
}, [items, search]);

    // Compute net worth by warehouse
    const netWorthByType = useMemo(() => {  // keep name same
    	const netWorth = {};
    	(filteredItems || []).forEach((i) => {
    		const warehouse = i?.warehouse ?? "Unknown";
    		const nw = Number(i?.netWorth) || 0;
    		if (!netWorth[warehouse]) netWorth[warehouse] = 0;
    		netWorth[warehouse] += nw;
    	});
    	return netWorth;
    }, [filteredItems]);
    
    // Group items by warehouse
    const groupedItems = useMemo(() => {
    	return (filteredItems || []).reduce((acc, item) => {
    		const warehouse = item?.warehouse ?? "Unknown";
    		if (!acc[warehouse]) acc[warehouse] = [];
    		acc[warehouse].push(item);
    		return acc;
    	}, {});
    }, [filteredItems]);
    
    // Paginate grouped items
    const paginatedGroups = useMemo(() => {
    	const pages = [];
    	let currentPage = [];
    	let currentCount = 0;
    
    	Object.entries(groupedItems || {}).forEach(([warehouse, warehouseItems]) => {
    		const itemsArray = Array.isArray(warehouseItems) ? warehouseItems : [];
    		if (itemsArray.length > pageSize) {
    			for (let i = 0; i < itemsArray.length; i += pageSize) {
    				const chunk = itemsArray.slice(i, i + pageSize);
    				pages.push([[warehouse, chunk]]);
    			}
    			return;
    		}
    		if (currentCount + itemsArray.length > pageSize && currentPage.length > 0) {
    			pages.push(currentPage);
    			currentPage = [];
    			currentCount = 0;
    		}
    		currentPage.push([warehouse, itemsArray]);
    		currentCount += itemsArray.length;
    	});
    
    	if (currentPage.length > 0) pages.push(currentPage);
    	return pages;
    }, [groupedItems, pageSize]);
    
    const totalPages = paginatedGroups.length;
    const currentGroups = paginatedGroups[page - 1] || [];


	const warehouseSummary = useMemo(() => {
		const summary = {};
		(filteredItems || []).forEach((item) => {
			const warehouse = item?.warehouse ?? "Unknown";
			const type = item?.type ?? "Unknown";
			const nw = Number(item?.netWorth) || 0;

			if (!summary[warehouse]) summary[warehouse] = { totalNetWorth: 0, types: {} };
			summary[warehouse].totalNetWorth += nw;

			if (!summary[warehouse].types[type]) summary[warehouse].types[type] = 0;
			summary[warehouse].types[type] += nw;
		});

		return Object.entries(summary).map(([warehouse, data]) => ({
			warehouse,
			totalNetWorth: data.totalNetWorth,
			types: data.types,
		}));
	}, [filteredItems]);

	return (
		<div className="w-full  flex-1 overflow-y-auto p-4">
		              {/* WH Summary*/}
  			<div className="flex flex-col md:flex-row gap-4 mb-3">
  				<div className="flex-1 overflow-x-auto">

                    <table className="min-w-1/2 border border-gray-600">
  						<thead>
  							<tr className="bg-gray-100">
  								<th className="border border-gray-300 px-3 py-2 text-center">Warehouse</th>
  								<th className="border border-gray-300 px-3 py-2 text-center">Total Net Worth</th>
  								<th className="border border-gray-300 px-3 py-2 text-center">Types</th>
  							</tr>
  						</thead>
  						<tbody>
  							{warehouseSummary.map((w, idx) => (
  								<tr key={w.warehouse} className={`hover:bg-gray-200 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
  									<td className="border border-gray-300 px-3 py-2">{w.warehouse}</td>
  									<td className="border border-gray-300 px-3 py-2 text-center">{w.totalNetWorth?.toLocaleString()} AED</td>
  									<td className="border border-gray-300 px-3 py-2">{Object.keys(w.types).join(", ")}</td>
  								</tr>
  							))}
  						</tbody>
  					</table>
  				</div>
  			</div>
      			
		
		            {/* Top row: Download & Used Units/Regions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-5">
                {/* Left: Units and Regions */}
                <div className="p-2 border rounded-xl bg-white shadow-sm h-full max-w-4xl">
                    <h2 className="text-lg font-semibold text-gray-800 mb-1">
                        Used Quantity/Units & Warehouses
                    </h2>
                    <span className="text-xs text-gray-400 mb-3 block">
                        Please use same letters as below or add new unit.
                    </span>

                    {/* Units */}
                    <div className="flex flex-wrap gap-2">
                        {[...new Set(items.map(i => i.quantity))].map((type, idx) => (
                            <span
                                key={idx}
                                className="px-3 py-1 rounded-full bg-green-100 text-green-700 font-bold text-sm shadow-sm"
                            >
                                {type}
                            </span>
                        ))}
                    </div>

                    {/* Regions */}
                    <div className="pt-4 flex flex-wrap gap-2">
                        {[...new Set(items.map(i => i.warehouse))].map((type, idx) => (
                            <span
                                key={idx}
                                className="px-3 py-1 rounded-full bg-green-100 text-green-700 font-bold text-sm shadow-sm"
                            >
                                {type}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Right: Exporter */}
                <div className="h-full">
                    <ReportExporter
						title="Export Inventory"
						data={items}
						showDate={false}
						columns={[
							{ label: "Name", field: "name" },
							{ label: "Type/Category", field: "type" },
							{ label: "Unit Price", field: "price" },
							{ label: "Quantity", field: "quantity" },
							{ label: "Warehouse", field: "warehouse" },
							{ label: "Net Worth", field: "netWorth" },
							{ label: "VAT", field: "vat" },
						]}
						filterableFields={{ type: "Categories", warehouse: "Warehouses", quantity: "Quantity" }}
					/>
                </div>
            </div>

  
			<div className="flex flex-col sm:flex-row items-center justify-between my-4 gap-2">
				<input
					type="text"
					placeholder="Search by name, type, or warehouse..."
					className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring focus:ring-blue-200 flex-1"
					value={search}
					onChange={(e) => { setSearch(e.target.value); setPage(1); }}
				/>
                    <button
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
                        onClick={() => setShowStaging(prev => !prev)}
                    >
                        Batch Upload
                        <span
                            className={`ml-2 transform transition-transform duration-200 ${
                                showStaging ? "rotate-180" : "rotate-0"
                            }`}
                        >
                            ▼
                        </span>
                    </button>
                                
				<button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer" onClick={() => setShowModal(true)}>Add Item</button>
			</div>

			<div className="overflow-x-auto rounded-md border border-gray-200">
				{loading ? (
					<div className="w-full flex"><p className="text-blue-400 italic text-xl w-full">Loading...</p></div>
				) : currentGroups.length === 0 ? (
					<table className="min-w-full divide-y divide-gray-100 text-sm md:text-base">
						<tbody>
							<tr>
								<td colSpan={8} className="px-3 py-4 text-center text-gray-500 italic">No items found</td>
							</tr>
						</tbody>
					</table>
				) : (
					<table className="min-w-full divide-y divide-gray-100 text-sm md:text-base">
						<thead className="bg-gray-50">
							<tr>
								<th className="px-3 py-4 text-left font-medium text-gray-700 w-12">#</th>
								<th className="px-3 py-4 text-center font-medium text-gray-700">Name</th>
								<th className="px-3 py-4 text-center font-medium text-gray-700">Type/Category</th>
								<th className="px-3 py-4 text-right font-medium text-gray-700">Unit Price</th>
								<th className="px-3 py-4 text-right font-medium text-gray-700">Quantity</th>
								<th className="px-3 py-4 text-center font-medium text-gray-700">Warehouse</th>
								<th className="px-3 py-4 text-right font-medium text-gray-700">Net worth</th>
								<th className="px-3 py-4 text-right font-medium text-gray-700">VAT (5%)</th>
								<th className="px-3 py-4 text-center font-medium text-gray-700">Actions</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-gray-100">
							{currentGroups.map(([type, itemsInType]) => (
								<React.Fragment key={type}>
									{(itemsInType || []).map((item, idx) => (
										<tr key={item?.id ?? idx} className="hover:bg-gray-300">
											<td className="px-3 py-2 text-gray-600">{idx + 1}</td>
											<td className="px-3 py-2 font-medium text-gray-800 truncate">{item?.name ?? ""}</td>
											<td className="px-3 py-2 text-gray-600">{item?.type ?? ""}</td>
											<td className="px-3 py-2 text-gray-600 text-right">{Number(item?.price ?? 0).toLocaleString()} AED</td>
											<td className="px-3 py-2 text-gray-600 text-right">{item?.quantity ?? 0}</td>
											<td className="px-3 py-2 text-gray-600">{item?.warehouse ?? ""}</td>
											<td className="px-3 py-2 text-gray-600 text-right">{item?.netWorth?.toLocaleString() ?? ""} AED</td>
											<td className="px-3 py-2 text-gray-600 text-right">{item?.netWorth ? (item.netWorth * 0.05).toLocaleString() : ""} AED</td>
											<td className="px-3 py-2 text-center">
												<button className="px-2 py-1 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 cursor-pointer" onClick={() => handleDelete(item?.id)}>Delete</button>
											</td>
										</tr>
									))}
									<tr className="bg-gray-100">
										<td colSpan={8} className="px-3 py-2 font-semibold text-left text-gray-800">Warehouse {type} — Net Worth: {Number(netWorthByType[type] ?? 0).toLocaleString()} AED</td>
									</tr>
								</React.Fragment>
							))}
						</tbody>
					</table>
				)}
			</div>

			{totalPages > 1 && (
				<div className="flex items-center justify-center gap-2 mt-3">
					<button className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer" onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page === 1}>Prev</button>
					<span className="text-sm text-gray-700">Page {page} of {totalPages}</span>
					<button className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer" onClick={() => setPage((p) => Math.min(p + 1, totalPages))} disabled={page === totalPages}>Next</button>
				</div>
			)}



			{showModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
					<div className="bg-white w-full max-w-md rounded-md shadow-lg p-6 relative">
						<h2 className="text-lg font-semibold mb-4">Add New Item</h2>
						<div className="flex flex-col gap-3">
							{["name", "type", "price", "quantity", "netWorth", "warehouse"].map((field) => {
								const isNumberField = ["price", "netWorth"].includes(field);
								return isNumberField ? (
									<FormattedNumberInput
										key={field}
										value={newItem[field]}
										onChange={(val) => setNewItem({ ...newItem, [field]: val })}
										placeholder={placeholdersForModal[field] || placeholdersForModal.default}
										className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${errors[field] ? "border-red-500" : "border-gray-300"}`}
									/>
								) : (
									<input
										key={field}
										type="text"
										placeholder={placeholdersForModal[field] || placeholdersForModal.default}
										className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${errors[field] ? "border-red-500" : "border-gray-300"}`}
										value={newItem[field] ?? ""}
										onChange={(e) => setNewItem({ ...newItem, [field]: e.target.value })}
									/>
								);
							})}
						</div>
						<div className="flex justify-end gap-2 mt-4">
							<button className="px-4 py-2 rounded-md border hover:bg-gray-100 cursor-pointer" onClick={() => { setShowModal(false); setErrors({}); }}>Cancel</button>
							<button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer" onClick={() => handleAdd(newItem)}>Add</button>
						</div>
						<button className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer" onClick={() => { setShowModal(false); setErrors({}); }}>✕</button>
					</div>
				</div>
			)}
			
			{/* Staging Table */}
        <StagingTable
        	ref={stagingRef}
        	visible={showStaging}
        	setVisible={setShowStaging}
        	apiProp={`${apiBaseUrl}/api/inventory/batch`}
        	entryDict={Object.fromEntries(
        		["name", "type", "price", "quantity", "netWorth", "warehouse"].map((key) => {
        			let type = "input";
        			let additionalTypes = null;
        			const label = fieldLabels[key] ?? key;
        
        			// Only price and netWorth are numbers
        			if (key === "price" || key === "netWorth") {
        				additionalTypes = "number";
        			}
        
        			return [key, { type, label, additionalTypes }];
        		})
        	)}
        />
        
        			
		</div>
	);
};

export default UsersInventory;
